import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { CheckCircleIcon as CheckCircle, CircleIcon as Circle, HouseIcon as House, SparkleIcon as Sparkle, GraduationCapIcon as GraduationCap, TruckIcon as Truck } from "@phosphor-icons/react";

/**
 * "Bring your dog home" tracker — rendered on the buyer Dashboard for
 * every released escrow. Shows a vertical milestone timeline with
 * checkboxes the buyer can tick as they complete each step. The
 * "First week home" milestone auto-unlocks the Dog Log subscription
 * nudge — natural transition from one-time purchase to ongoing
 * platform engagement.
 */
export default function HomeTrackerWidget() {
  const [trackers, setTrackers] = useState(null);

  const load = () => {
    api.get("/me/home-tracker", { silent: true })
      .then((r) => setTrackers(r.data || []))
      .catch(() => setTrackers([]));
  };
  useEffect(load, []);

  if (trackers === null) return null;
  if (trackers.length === 0) return null;

  return (
    <section className="mb-8" data-testid="home-tracker-widget">
      <div className="flex items-center gap-2 mb-3">
        <House size={20} weight="duotone" className="text-crimson" />
        <h2 className="font-spectral text-2xl tracking-tight">Bring your dog home</h2>
      </div>
      <div className="space-y-4">
        {trackers.map((t) => <TrackerCard key={t.id} t={t} onChange={load} />)}
      </div>
    </section>
  );
}

function TrackerCard({ t, onChange }) {
  const [busy, setBusy] = useState("");

  const toggle = async (key, current) => {
    setBusy(key);
    try {
      if (current) {
        await api.post(`/me/home-tracker/${t.id}/milestone/${key}/unmark`);
      } else {
        await api.post(`/me/home-tracker/${t.id}/milestone/${key}/mark`, {});
      }
      onChange();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const dogName = t.dog?.name || "Your puppy";
  const photo = (t.dog?.photos && t.dog.photos[0]) || null;
  const breed = t.dog?.breed || "";
  const pct = Math.round((t.progress.complete / t.progress.total) * 100);

  return (
    <article
      className="rounded-xl border-2 border-[#EAEAEA] bg-white p-5 shadow-sm"
      data-testid={`home-tracker-card-${t.id}`}
    >
      <header className="flex items-start gap-4 mb-4">
        <div className="w-16 h-16 rounded-lg bg-[#FBF7EC] overflow-hidden border border-[#EAEAEA] shrink-0 flex items-center justify-center">
          {photo ? (
            <img src={photo} alt={dogName} className="w-full h-full object-cover" />
          ) : (
            <House size={22} className="text-[#C9A227]" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-spectral text-lg tracking-tight">{dogName}</h3>
          <p className="text-[12px] text-[#6B6B6B]">
            {breed}{breed && t.seller?.name ? " · " : ""}{t.seller?.name || ""}
          </p>
          <div className="mt-2 h-2 rounded-full bg-[#F0F0F0] overflow-hidden">
            <div
              className="h-2 bg-[#3DA56E] transition-all"
              style={{ width: `${pct}%` }}
              data-testid={`home-tracker-progress-${t.id}`}
            />
          </div>
          <p className="text-[11px] text-[#6B6B6B] mt-1 font-tabular">
            {t.progress.complete} / {t.progress.total} milestones complete
          </p>
        </div>
      </header>

      <ol className="space-y-2">
        {t.milestone_order.map((key) => {
          const m = t.milestones?.[key];
          const isComplete = m?.status === "complete";
          const isAuto = t.milestone_auto[key];
          const isPaidInFull = key === "paid_in_full";
          const canToggle = !isAuto || (key === "first_week_home" && isComplete);
          return (
            <li key={key} className="flex items-start gap-3">
              <button
                type="button"
                disabled={!canToggle || busy === key || isPaidInFull}
                onClick={() => toggle(key, isComplete)}
                aria-label={isComplete ? "Mark incomplete" : "Mark complete"}
                data-testid={`home-tracker-${t.id}-toggle-${key}`}
                className={`mt-0.5 shrink-0 transition-colors ${
                  isComplete ? "text-[#3DA56E]" : "text-[#C8C8C8] hover:text-ink"
                } ${(isAuto || isPaidInFull) ? "cursor-default" : "cursor-pointer"}`}
              >
                {isComplete ? <CheckCircle size={22} weight="fill" /> : <Circle size={22} weight="regular" />}
              </button>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${isComplete ? "text-[#3F3F3F] line-through decoration-[#3DA56E]/40" : "text-ink"}`}>
                  {t.milestone_labels[key]}
                  {isAuto && <span className="ml-1 text-[9px] uppercase tracking-wider text-[#6B6B6B] align-middle">auto</span>}
                </p>
                <p className="text-[12px] text-[#6B6B6B] leading-snug">{t.milestone_blurbs[key]}</p>
                {key === "first_week_home" && isComplete && (
                  <Link
                    to="/dog-log"
                    className="inline-flex items-center gap-1 mt-1 text-[12px] font-bold text-crimson hover:underline"
                    data-testid={`home-tracker-${t.id}-doglog-cta`}
                  >
                    <Sparkle size={12} weight="fill" /> Start tracking care in Dog Log →
                  </Link>
                )}
                {/* Cross-persona handoff: when buyer marks the dog as
                    picked up, surface local Service Providers (trainers,
                    groomers, walkers, sitters). High-intent moment — new
                    dog owners actively shop these in week 1-4. */}
                {key === "picked_up" && isComplete && (
                  <Link
                    to="/find-pros"
                    className="inline-flex items-center gap-1 mt-1 text-[12px] font-bold text-ink hover:text-crimson hover:underline"
                    data-testid={`home-tracker-${t.id}-find-pros-cta`}
                  >
                    <GraduationCap size={12} weight="duotone" /> Book a trainer, groomer, walker, or sitter →
                  </Link>
                )}
                {/* Travel-arranged milestone surfaces transport finder
                    if the buyer hasn't already booked. */}
                {key === "travel_arranged" && !isComplete && (
                  <Link
                    to="/transport"
                    className="inline-flex items-center gap-1 mt-1 text-[12px] font-bold text-ink hover:text-crimson hover:underline"
                    data-testid={`home-tracker-${t.id}-transport-cta`}
                  >
                    <Truck size={12} weight="duotone" /> Get a transport quote →
                  </Link>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </article>
  );
}
